import { Autocomplete, Box, Paper, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import Subheading from "../../../components/common/Subheading";

/**
 * DataClassification component for the Auto4Client feature.
 *
 * This component renders an autocomplete dropdown within a grid item to select a task ID
 * and updates data classification inputs accordingly.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array<Object>} props.mongoTaskData - Array of objects containing task data.
 * @param {Function} props.onInputsChange - Callback function to notify parent about state changes.
 *
 * @returns {JSX.Element} - The JSX element for the auto-complete dropdown within a grid item.
 */
function DataClassification({ mongoTaskData, onInputsChange }) {
  const [inputs, setInputs] = useState({
    mongoTaskID: "",
  });

  // State to hold the selected task data
  const [selectedTaskData, setSelectedTaskData] = useState(null);

  // Notify parent component when state changes
  useEffect(() => {
    onInputsChange(inputs);
  }, [inputs, onInputsChange]);

  const handleTaskIdChange = (event, value) => {
    setInputs((prevState) => ({
      ...prevState,
      mongoTaskID: value,
    }));

    // Find the selected task data based on task ID
    if (value) {
      const taskData = mongoTaskData.find((task) => task["task-id"] === value);
      setSelectedTaskData(taskData);
    } else {
      setSelectedTaskData(null);
    }
  };

  return (
    <Grid container direction="column" spacing={4} p={3} mx={"10%"}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Subheading text="Task ID Selection" sx={{ mb: 3 }} />
        <Box sx={{ mt: 2 }}>
          <Autocomplete
            options={mongoTaskData.map((x) => x["task-id"])}
            renderInput={(params) => <TextField {...params} label="Select Task ID" />}
            onChange={handleTaskIdChange}
            fullWidth
            size="medium"
          />
        </Box>
      </Paper>

      {/* Always display the Task Input Data section */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
        <Subheading text="Task Input Data" sx={{ mb: 3 }} />
        <Box sx={{ mt: 2, maxHeight: "400px", overflow: "auto" }}>
          {selectedTaskData ? (
            <pre
              style={{
                whiteSpace: "pre-wrap",
                // backgroundColor: "#f5f5f5",
                padding: "16px",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              {JSON.stringify(selectedTaskData, null, 2)}
            </pre>
          ) : (
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                padding: "16px",
                fontStyle: "italic"
              }}
            >
              Please select a Task ID to view its data
            </Typography>
          )}
        </Box>
      </Paper>
    </Grid>
  );
}

export default DataClassification;